import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HelpCircle, ArrowRight, Sparkles, GraduationCap, Wrench } from "lucide-react";

const IMG = "https://static.prod-images.emergentagent.com/jobs/1304f033-c917-4974-91b0-9d4153e7c191/images/bbd5bb9c27358ef88df788e6770270a1121c5a8dc0682cb66f5061d54aabd71a.jpeg";

const QUESTIONS = [
  "Pourquoi un ordinateur comprend-il nos ordres ?",
  "Comment reconnaître une information fiable sur Internet ?",
  "Que se passe-t-il quand on publie une photo en ligne ?",
  "Comment fonctionne la reconnaissance faciale ?",
];

const OBJECTIVES = [
  {
    icon: Sparkles,
    c: "bg-sky-100 text-sky-600",
    title: "1. Donner le goût du numérique et de l'IA aux plus jeunes",
    intro:
      "…pour que la société de demain dispose de citoyens éclairés et de talents en nombre suffisant.",
    paras: [
      "Les secteurs du numérique et de l'intelligence artificielle connaissent une croissance exponentielle, mais les filières informatiques peinent à attirer suffisamment d'étudiants, en particulier féminins. Or notre société numérique aura un besoin croissant de développeurs, de data scientists, d'experts en cybersécurité et de citoyens capables de comprendre les enjeux de ces technologies.",
      "Nous sommes persuadés que pour inverser cette tendance il faut agir en amont, au début du processus d'apprentissage.",
      "Nous pensons que si l'on parvient à émerveiller les enfants, à éveiller en eux le goût pour le numérique et la découverte, ces mêmes enfants auront un a priori positif à l'égard des options informatiques dans l'enseignement secondaire, et par la suite à l'université ou dans l'enseignement supérieur.",
    ],
  },
  {
    icon: GraduationCap,
    c: "bg-amber-100 text-amber-600",
    title: "2. Contribuer à la formation générale des enfants au numérique et aux enjeux de l'IA",
    paras: [
      "À travers nos activités, les enfants expérimentent et s'interrogent sur des phénomènes numériques qui leur sont proches. Qu'est-ce qu'un algorithme ? Comment fonctionne une recommandation sur une plateforme vidéo ? Que devient mon mot de passe quand je le saisis ? Comment un chatbot génère-t-il une réponse ?",
      "En observant, en expérimentant, en demandant à l'enfant de résoudre des défis (comme le célèbre « Jeu Kwarizmix » ou « Le Code du Chevalier »), nous essayons de répondre à ces questions et d'en susciter de nouvelles. Tous les enfants passent par le stade de développement du « pourquoi ? », nous pensons que les expériences numériques que nous proposons permettent de structurer et de développer cette capacité de s'interroger sur les phénomènes et les choses.",
    ],
  },
  {
    icon: Wrench,
    c: "bg-rose-100 text-rose-600",
    title:
      "3. Développer les savoirs, savoir-faire et savoir-être indispensables à une pratique éclairée du numérique",
    paras: [
      "En animation, chaque enfant dispose de son propre matériel (PC ou tablette) ou d'activités « hors ligne » (papier, cartes, jeux de rôle) pour relever un défi, faire une observation, réaliser une expérience.",
      "Chaque enfant travaille donc à son propre rythme en suivant son propre chemin de raisonnement. Ce faisant, il développe divers savoir-faire :",
    ],
    list: [
      "Précision de l'observation et de la description d'un phénomène numérique",
      "Précision du raisonnement logique (comprendre l'enchaînement des instructions)",
      "Apprentissage de la rigueur algorithmique (décomposer un problème en étapes simples et tester chaque étape pour valider ou corriger le programme)",
    ],
  },
];

export default function Presentation() {
  return (
    <div data-testid="presentation-page">
      <section className="bg-gradient-to-b from-sky-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Notre univers · Présentation</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-2">
              Qui sommes-nous ?
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Kwarizmix est une ASBL active en Fédération Wallonie-Bruxelles depuis 2020.
              Fondée par des experts en technologies éducatives, elle propose des ateliers
              d'initiation au numérique et à l'intelligence artificielle pour les enfants tout au
              long de l'année.
            </p>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-[2rem] overflow-hidden shadow-2xl shadow-sky-500/20 rotate-2">
            <img src={IMG} alt="Présentation Kwarizmix" className="w-full h-[360px] object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-5 text-slate-700 text-lg leading-relaxed" data-testid="presentation-intro">
        <p>
          À travers une approche pédagogique originale, elle initie les jeunes à la pensée
          computationnelle et à une utilisation critique du numérique. Avec Kwarizmix, ce sont eux
          qui formulent les hypothèses, testent les algorithmes, analysent les résultats et les
          confrontent avec ceux d'autres jeunes.
        </p>
        <p>
          Kwarizmix assure également la conception d'activités numériques sur mesure pour le compte
          d'organismes publics ou privés et participe à de nombreux événements de promotion du
          numérique tels que le I Love Science Festival, la Fête de l'Iris, les Startech's Days ou
          encore le Printemps de Sciences.
        </p>
        <p>
          Les ateliers d'Kwarizmix sont bâtis par et pour les enfants. Le rôle de l'animateur
          consiste essentiellement à mettre à l'épreuve leurs capacités d'observation, leur logique
          et leur esprit critique, pour les amener à mieux comprendre le monde numérique qui les
          entoure.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-sky-50 rounded-3xl p-8 border border-sky-100">
          <ul className="space-y-3">
            {QUESTIONS.map((q) => (
              <li key={q} className="flex items-start gap-3 text-slate-800 font-medium">
                <HelpCircle className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
                <span>{q}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-slate-600 italic">
            …sont autant de questions auxquelles ils trouveront réponse en participant à nos ateliers !
          </p>
        </div>
      </section>

      <section className="bg-slate-50 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-slate-900 mb-2">Notre mission</h2>
          <p className="text-slate-600 mb-10">Kwarizmix poursuit trois objectifs majeurs :</p>
          <div className="space-y-6">
            {OBJECTIVES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.div
                  key={o.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8"
                  data-testid={`objective-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <span className={`grid place-items-center w-12 h-12 rounded-2xl shrink-0 ${o.c}`}>
                      <Icon className="w-6 h-6" />
                    </span>
                    <div>
                      <h3 className="font-heading font-extrabold text-xl text-slate-900">{o.title}</h3>
                      {o.intro && <p className="mt-1 text-slate-600 italic">{o.intro}</p>}
                    </div>
                  </div>
                  <div className="mt-4 space-y-3 text-slate-700 leading-relaxed">
                    {o.paras.map((p, j) => (
                      <p key={`${o.title}-p${j}`}>{p}</p>
                    ))}
                    {o.list && (
                      <ul className="list-disc pl-5 space-y-1.5 marker:text-sky-500">
                        {o.list.map((li) => (
                          <li key={li}>{li}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <Link to="/notre-univers/approche" data-testid="presentation-to-approche" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-105 transition-[transform,background-color]">
          Découvrir notre approche <ArrowRight className="w-5 h-5" />
        </Link>
      </section>
    </div>
  );
}
